/* Handoff to the `@ai21/gateway` CLI: the page prints a command naming the agent, and the
 * CLI signs the reader in and creates the key. Nothing talks to the CLI; the gateway
 * confirms — see useAgentUsedPoll. */
import { type AgentKind } from "../../agents/kindRegistry";
import {
  GATEWAY_BASE_URL,
  GATEWAY_URL_IS_PLACEHOLDER,
} from "../../services/gatewayUrl";

/** A plain http(s) URL with nothing a shell would read as syntax — this is pasted into a terminal. */
function isPlainHttpUrl(value: string): boolean {
  try {
    const { protocol } = new URL(value);
    if (protocol !== "http:" && protocol !== "https:") return false;
  } catch {
    return false;
  }

  return /^[A-Za-z0-9:/._~%-]+$/.test(value);
}

/** Ids we are willing to paste into a terminal: no whitespace, quotes or shell syntax. */
function isPlainId(value: string): boolean {
  return /^[A-Za-z0-9._@:-]+$/.test(value);
}

/** What the CLI accepts for `--workspace` (its `isWorkspaceId`), which is narrower than
 *  isPlainId: an id it would reject has to be left off, not printed into a failing command. */
function isCliWorkspaceId(value: string): boolean {
  return (
    /^[A-Za-z0-9._-]{1,64}$/.test(value) && value !== "." && value !== ".."
  );
}

/** The `--client` name the CLI knows each kind by; missing means the CLI cannot configure it. */
const CLI_CLIENTS: Partial<Record<AgentKind, string>> = {
  claude_code: "claude-code",
  codex: "codex",
};

export function cliClientFor(kind: AgentKind): string | undefined {
  return CLI_CLIENTS[kind];
}

/** Whether this agent's id can go into the command at all. The wizard derives its own ids
 *  with toAgentId(), but the adopt-an-existing and reconnect paths print an id the server
 *  gave us — and this string is pasted into a shell. */
export function cliCanPrintAgentId(agentId: string): boolean {
  return isPlainId(agentId);
}

/** `@ai21/gateway` writes only Claude Code's settings.json and Codex's config.toml, so
 *  offering it for another kind would hand the user a command that configures nothing. */
export function cliCanConfigure(kind: AgentKind): boolean {
  return cliClientFor(kind) !== undefined;
}

/** Whether the reader is offered the command at all. When they are, the CLI creates the
 *  key, so the webapp mints one only if they turn to the snippet instead. */
export function cliRouteOffered(kind: AgentKind, agentId: string): boolean {
  return cliCanConfigure(kind) && cliCanPrintAgentId(agentId);
}

/** The page prints no key, so it needs a CLI that signs in and creates one itself. */
export const CLI_PACKAGE = "@ai21/gateway";

const CLI_DEFAULT_GATEWAY_URL = "https://api.ai21.com/gateway";

/** True for the URL the CLI would pick anyway, so the flag can be left off. */
function isCliDefaultGateway(url: string): boolean {
  return url.replace(/\/+$/, "") === CLI_DEFAULT_GATEWAY_URL;
}

/** The command to run: what to configure and who for, never a key — the CLI signs in and
 *  creates one in `--workspace`. `--user-id` keeps the snippet's per-user attribution; `--gateway` only off production, which is what it means.
 *  `--user` writes ~/.claude/settings.json instead of the CLI's default, the current repo's
 *  .claude/settings.json — without it the routing only applies inside the folder the command
 *  happened to run in, which reads as "nothing happened" everywhere else. */
export function cliCommand(
  agentId: string,
  {
    userId,
    workspaceId,
    client,
    gatewayUrl = GATEWAY_URL_IS_PLACEHOLDER ? undefined : GATEWAY_BASE_URL,
  }: {
    userId?: string;
    workspaceId?: string;
    client?: string;
    gatewayUrl?: string;
  } = {},
): string {
  // Nothing to print without the one flag that names what to configure.
  if (!cliCanPrintAgentId(agentId)) return "";

  const flags = [`--agent-id ${agentId}`];
  // Each dropped rather than printed unquoted; the CLI defaults all four.
  if (userId !== undefined && userId !== "" && isPlainId(userId)) {
    flags.push(`--user-id ${userId}`);
  }

  if (workspaceId !== undefined && isCliWorkspaceId(workspaceId)) {
    flags.push(`--workspace ${workspaceId}`);
  }

  if (client !== undefined && isPlainId(client)) {
    flags.push(`--client ${client}`);
  }

  if (
    gatewayUrl !== undefined &&
    isPlainHttpUrl(gatewayUrl) &&
    !isCliDefaultGateway(gatewayUrl)
  ) {
    flags.push(`--gateway ${gatewayUrl}`);
  }

  return `npx ${CLI_PACKAGE} install ${flags.join(" ")} --user`;
}
